import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import logger from '../utils/logger';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true); // Chargement de l'abonnement en cours
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    let timeoutId;
    let mounted = true;
    let authSubscription = null;

    // Check if Supabase is properly configured
    const isSupabaseConfigured =
      import.meta.env.VITE_SUPABASE_URL &&
      import.meta.env.VITE_SUPABASE_ANON_KEY &&
      import.meta.env.VITE_SUPABASE_URL !== 'https://placeholder.supabase.co';

    // If Supabase is not configured, skip auth initialization
    if (!isSupabaseConfigured) {
      logger.info('ℹ️ Supabase not configured - skipping auth initialization');
      setLoading(false);
      return;
    }

    // Safety timeout: force loading to false after 5 seconds
    // Réduit de 10s à 5s pour une meilleure UX
    timeoutId = setTimeout(() => {
      if (mounted) {
        logger.warn('⚠️ Auth initialization timeout (5s) - forcing loading to false');
        logger.warn('This usually means Supabase connection is slow or failing');
        setLoading(false);
      }
    }, 5000);

    // Initialize auth with better error handling
    const initAuth = async () => {
      try {
        logger.info('🔐 Initializing authentication...');

        // Get initial session with timeout protection
        const { data: { session }, error } = await supabase.auth.getSession();

        if (!mounted) return;

        if (error) {
          logger.error('❌ Error getting session:', error);
          setSession(null);
          setUser(null);
        } else {
          logger.info(session ? '✅ Session found' : 'ℹ️ No active session');
          setSession(session);
          setUser(session?.user ?? null);

          // Fetch subscription and admin status in background - don't block auth initialization
          if (session?.user) {
            fetchSubscription(session.user.id).catch(err => {
              logger.error('Error fetching subscription in background:', err);
            });
            checkAdminStatus(session.user.id).catch(err => {
              logger.error('Error checking admin status in background:', err);
            });
          } else {
            // Pas d'utilisateur = pas de chargement d'abonnement
            setSubscriptionLoading(false);
          }
        }
      } catch (error) {
        if (!mounted) return;
        logger.error('❌ Unexpected error during auth initialization:', error);
        setSession(null);
        setUser(null);
      } finally {
        if (!mounted) return;
        clearTimeout(timeoutId);
        setLoading(false);
        logger.info('✅ Auth initialization complete');
      }
    };

    // Start initialization
    initAuth();

    // Listen for auth changes
    try {
      const { data } = supabase.auth.onAuthStateChange((event, session) => {
        if (!mounted) return;

        logger.info('🔄 Auth state changed:', event);

        setSession(session);
        setUser(session?.user ?? null);

        // Fetch subscription and admin status in background
        if (session?.user) {
          fetchSubscription(session.user.id).catch(err => {
            logger.error('Error fetching subscription on auth change:', err);
          });
          checkAdminStatus(session.user.id).catch(err => {
            logger.error('Error checking admin status on auth change:', err);
          });
        } else {
          setSubscription(null);
          setIsAdmin(false);
        }
      });

      authSubscription = data?.subscription;
    } catch (error) {
      logger.error('❌ Error setting up auth listener:', error);
    }

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      if (authSubscription) {
        authSubscription.unsubscribe();
      }
    };
  }, []);

  const fetchSubscription = async (userId) => {
    setSubscriptionLoading(true);
    try {
      logger.info('🔍 Fetching subscription for user:', userId);

      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1);

      // 42P01 = table does not exist
      if (error && error.code !== '42P01') {
        logger.error('Error fetching subscription:', error);
        return;
      }

      // If table doesn't exist, we'll just not set any subscription
      if (error && error.code === '42P01') {
        logger.warn('Subscriptions table does not exist. Please run migrations.');
        return;
      }

      const subscriptionRow = Array.isArray(data) ? data[0] : null;
      logger.info('📋 Subscription data:', subscriptionRow);
      logger.info('📋 Subscription status:', subscriptionRow?.status);
      setSubscription(subscriptionRow || null);
    } catch (error) {
      logger.error('Error fetching subscription:', error);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const checkAdminStatus = async (userId) => {
    try {
      const { data, error } = await supabase.rpc('is_admin', {
        p_user_id: userId,
      });
      if (error) {
        // Function may not exist yet if migration hasn't run
        if (error.code !== '42883') { // 42883 = function does not exist
          logger.error('Error checking admin status:', error);
        }
        setIsAdmin(false);
        return;
      }
      setIsAdmin(data === true);
    } catch (error) {
      logger.error('Error checking admin status:', error);
      setIsAdmin(false);
    }
  };

  const signInWithEmail = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  };

  const signUpWithEmail = async (email, password, options = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options,
    });
    return { data, error };
  };

  const signInWithGoogle = async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    return { data, error };
  };

  const signInWithFacebook = async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'facebook',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    return { data, error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (!error) {
      setSubscription(null);
      setIsAdmin(false);
    }
    return { error };
  };

  const resetPassword = async (email) => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { data, error };
  };

  const updatePassword = async (newPassword) => {
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    return { data, error };
  };

  const hasActiveSubscription = () => {
    if (!subscription) return false;

    const status = subscription.status;
    return status === 'active' || status === 'trialing';
  };

  const refreshSubscription = useCallback(() => {
    if (user) {
      return fetchSubscription(user.id);
    }
    return null;
  }, [user]);

  const value = {
    user,
    session,
    loading,
    subscriptionLoading,
    subscription,
    isAdmin,
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    signInWithFacebook,
    signOut,
    resetPassword,
    updatePassword,
    hasActiveSubscription,
    refreshSubscription,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
